/**
 * D11 — Thank You.
 *
 * Two departures from the artwork, both deliberate:
 *
 * The checkout stepper is deliberately absent: every step is behind the donor
 * now, and four filled circles above a "Thank You" only push the reference —
 * the one thing they need to keep — further down the screen.
 *
 * 1. The donation reference is shown. The design never displayed it (gap
 *    G-15), which left a guest donor with no way to find the donation again.
 * 2. The stepper reads as complete rather than still highlighting "Review"
 *    (gap G-01).
 *
 * The receipt line reflects what the server actually did — it is not a fixed
 * promise that an email was sent.
 */

import { useEffect } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { formatDate, formatMoney, formatPeople } from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { Button } from '../../components/common/Button';
import { SupportCard } from '../../components/layout/SupportCard';
import { Card } from '../../components/common/Card';
import { CheckIcon, Glyph } from '../../components/common/Icon';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Text } from '../../components/common/Text';
import { useAppConfig, useDonation } from '../../core/api/hooks';
import { useDonationStore } from '../donation/donationStore';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useHomeUnderneath } from '../../app/navigation/homeUnderneath';
import { useT } from '../../core/i18n/LanguageProvider';

export function ThankYouScreen({ navigation, route }: DonorScreenProps<'ThankYou'>) {
  const t = useT();
  const { donationId, publicReference } = route.params;
  const { data: donation } = useDonation(donationId);
  const reset = useDonationStore((s) => s.reset);
  const { data: config } = useAppConfig();

  // The donation is placed; the draft has served its purpose. Clearing it here
  // means "Order More" starts clean rather than resurrecting the last order.
  useEffect(() => () => reset(), [reset]);
  // The paid checkout leaves the history: back from here goes Home.
  useHomeUnderneath(navigation);

  return (
    <ScreenShell
      leading="none"
      testID="screen-thank-you"
      pinnedFooter={
        <View style={styles.actions}>
          <Button
            label={t('Order More')}
            onPress={() => {
              reset();
              navigation.navigate('DonateMeal');
            }}
            leadingIcon={<Glyph name="heart" size={18} />}
            testID="thankyou-cta-donate-more"
          />
          <Button
            label={t('Back to Home')}
            variant="secondary"
            onPress={() => navigation.navigate('Home')}
            leadingIcon={<Glyph name="home" size={18} />}
            testID="thankyou-cta-home"
          />
        </View>
      }
    >

      <View style={styles.hero}>
        <CheckIcon size={96} />
        <Text variant="screenTitle" tone="brand" align="center" style={styles.title}>{t('Thank You!')}</Text>
        <Text variant="bodyStrong" align="center">{t('Your order has been confirmed.')}</Text>
        <Text variant="body" tone="secondary" align="center" style={styles.blurb}>{t("You've just helped provide nutritious meals to those who need them the most.")}</Text>
      </View>

      {/* The reference is the donor's only handle on this donation if they are
          not signed in, so it is given prominence rather than buried.
          Tapping it opens tracking: the summary card that used to carry
          "View Details" is gone, and this was the only way through to it. */}
      <Pressable
        onPress={() => navigation.navigate('Tracking', { donationId })}
        accessibilityRole="button"
        accessibilityLabel={`Donation reference ${publicReference}. Track this donation.`}
        testID="reference-card"
        style={({ pressed }) => [styles.referenceCard, pressed && styles.referencePressed]}
      >
        <Text variant="caption" tone="secondary" align="center">{t('Your order reference')}</Text>
        <Text variant="screenTitle" tone="brand" align="center" selectable testID="reference">
          {publicReference}
        </Text>
        <Text variant="caption" tone="tertiary" align="center">{t('Tap to track your order.')}</Text>
      </Pressable>

      {donation?.donorName && donation.paymentMethod ? (
        <Card style={styles.card}>
          <View style={styles.receiptRow}>
            <Glyph name="donations" size={22} />
            <View style={styles.receiptText}>
              <Text variant="cardTitle">{t('Order Successful')}</Text>
              <Text variant="caption" tone="secondary">
                {donation.transactionReference
                  ? `Transaction ${donation.transactionReference}`
                  : 'Your payment is being processed.'}
              </Text>
            </View>
          </View>
        </Card>
      ) : null}


      <View style={styles.impactCard}>
        <Glyph name="care" size={28} />
        <View style={styles.impactText}>
          <Text variant="cardTitle" tone="brand">{t("You're Making an Impact!")}</Text>
          <Text variant="caption" tone="secondary">
            {t('Your kindness brings hope and fills plates. Thank you for being a part of this mission.')}
          </Text>
        </View>
      </View>

      {donation ? (
        <Text variant="caption" tone="tertiary" align="center" style={styles.footNote}>
          {formatPeople(donation.peopleCount)} {t('will be fed on')}{' '}
          {formatDate(donation.distributionDate)} · {formatMoney(donation.totalAmount)}
        </Text>
      ) : null}
      {/* Right after paying is when a donor most wants to be able to ask a
          question — about the delivery, the date, or just reassurance. */}
      <SupportCard
        support={config?.support}
        title={t('Need help with this order?')}
        reference={donation?.publicReference}
      />

    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  hero: {
    alignItems: 'center',
    gap: space.sm,
    paddingVertical: space.xl,
  },
  title: {
    marginTop: space.md,
  },
  blurb: {
    marginTop: space.xs,
    paddingHorizontal: space.md,
  },
  referenceCard: {
    gap: space.xs,
    padding: space.lg,
    borderRadius: radius.lg,
    backgroundColor: colors.bg.surface,
    borderWidth: 1,
    borderColor: colors.border.brand,
  },
  referencePressed: {
    opacity: 0.85,
  },
  card: {
    marginTop: space.lg,
  },
  receiptRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
  },
  receiptText: {
    flex: 1,
    gap: space.xxs,
  },
  impactCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    marginTop: space.lg,
    padding: space.lg,
    borderRadius: radius.lg,
    backgroundColor: colors.bg.brandSubtle,
  },
  impactText: {
    flex: 1,
    gap: space.xxs,
  },
  footNote: {
    marginTop: space.lg,
  },
  actions: {
    gap: space.sm,
  },
});
